"use client";

import { useEffect, useState, type FormEvent } from "react";
import { useTranslations } from "use-intl";
import { ActionButton, Field, Modal, SelectInput, TextInput } from "@/components/counter";
import { PosApiError } from "@/lib/api/client";
import { posStaffApi, type PosStaff } from "@/lib/api/staff";
import { resetPosContext } from "@/lib/hooks/usePosSession";
import { switchStaffPin, unlockStaffPin } from "@/components/shell/staffPinClient";

const PIN_SHAPE = /^\d{4,6}$/;

/**
 * Hands this till to a colleague, proven by their own PIN — a same-shift handoff, not a
 * new sign-in. A right PIN re-sets the session cookie server-side (`/pos-auth/pin-switch`) and this
 * dialog reloads the page so every screen reads the new session; a wrong one counts against that
 * colleague only, and a locked colleague can be unlocked right here by a manager's own PIN without
 * ever changing the colleague's PIN.
 */
export function StaffPinSwitchDialog({
  open,
  ownStaffId,
  onClose,
}: {
  open: boolean;
  /** The signed-in staff member's own login id, excluded from the colleague list. */
  ownStaffId?: string;
  onClose: () => void;
}) {
  const t = useTranslations("pinSwitch");
  const tc = useTranslations("common");

  const [staff, setStaff] = useState<PosStaff[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [colleague, setColleague] = useState("");
  const [pin, setPin] = useState("");
  const [shapeWrong, setShapeWrong] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();
  const [colleagueLocked, setColleagueLocked] = useState(false);
  const [step, setStep] = useState<"pin" | "unlock">("pin");
  const [manager, setManager] = useState("");
  const [managerPin, setManagerPin] = useState("");

  // Re-seed on every open, during render (see "You Might Not Need an Effect", same pattern as
  // DeskPinDialog): a dialog reopened for a second handoff must not still show the last attempt.
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    setPin("");
    setManagerPin("");
    setShapeWrong(false);
    setProblem(undefined);
    setColleagueLocked(false);
    setStep("pin");
  }

  useEffect(() => {
    if (!open || staff !== null) return;
    let alive = true;
    posStaffApi
      .list()
      .then((list) => {
        if (alive) setStaff(list);
      })
      .catch(() => {
        if (alive) setLoadFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [open, staff]);

  const colleagues = (staff ?? []).filter((s) => s.active && s.userId !== ownStaffId);

  function switchOutcomeMessage(outcome: string, attemptsRemaining: number | null): string {
    switch (outcome) {
      case "WRONG_PIN":
        return attemptsRemaining != null
          ? t("wrongPin", { count: attemptsRemaining })
          : t("wrongPinNoCount");
      case "LOCKED":
        return t("locked");
      case "NOT_ENROLLED":
        return t("notEnrolled");
      case "LOCATION_DENIED":
        return t("locationDenied");
      default:
        return t("genericProblem");
    }
  }

  async function submitSwitch(event?: FormEvent) {
    event?.preventDefault();
    if (busy || !colleague) return;
    if (!PIN_SHAPE.test(pin)) {
      setShapeWrong(true);
      return;
    }
    setShapeWrong(false);
    setProblem(undefined);
    setColleagueLocked(false);
    setBusy(true);
    try {
      const result = await switchStaffPin(colleague, pin);
      if (result.outcome === "SWITCHED") {
        resetPosContext();
        window.location.reload();
        return;
      }
      setPin("");
      setProblem(switchOutcomeMessage(result.outcome, result.attemptsRemaining));
      setColleagueLocked(result.outcome === "LOCKED");
    } catch (error) {
      setPin("");
      setProblem(error instanceof PosApiError ? error.problem.message : t("genericProblem"));
    } finally {
      setBusy(false);
    }
  }

  async function submitUnlock(event?: FormEvent) {
    event?.preventDefault();
    if (busy || !manager) return;
    if (!PIN_SHAPE.test(managerPin)) {
      setShapeWrong(true);
      return;
    }
    setShapeWrong(false);
    setProblem(undefined);
    setBusy(true);
    try {
      const result = await unlockStaffPin(manager, managerPin, colleague);
      setManagerPin("");
      if (result.outcome === "UNLOCKED_BY_MANAGER") {
        setProblem(t("unlock.done"));
        setColleagueLocked(false);
        setStep("pin");
      } else if (result.outcome === "WRONG_PIN") {
        setProblem(t("unlock.wrongPin"));
      } else {
        setProblem(switchOutcomeMessage(result.outcome, result.attemptsRemaining));
      }
    } catch (error) {
      setManagerPin("");
      setProblem(error instanceof PosApiError ? error.problem.message : t("genericProblem"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={step === "pin" ? t("title") : t("unlock.title")}
      description={step === "pin" ? t("body") : t("unlock.body")}
      width="max-w-md"
      footer={
        <>
          <ActionButton onClick={onClose} disabled={busy}>
            {tc("cancel")}
          </ActionButton>
          {step === "pin" ? (
            <ActionButton variant="primary" busy={busy} disabled={!colleague || pin.length === 0} onClick={() => submitSwitch()}>
              {t("switch")}
            </ActionButton>
          ) : (
            <ActionButton variant="primary" busy={busy} disabled={!manager || managerPin.length === 0} onClick={() => submitUnlock()}>
              {t("unlock.action2")}
            </ActionButton>
          )}
        </>
      }
    >
      {step === "pin" ? (
        <form onSubmit={submitSwitch} className="flex flex-col gap-4" data-testid="pin-switch-dialog">
          <Field label={t("colleague")}>
            {(id) =>
              staff === null && !loadFailed ? (
                <p id={id} className="text-sm text-text-weak">
                  {t("loadingColleagues")}
                </p>
              ) : colleagues.length === 0 ? (
                <p id={id} className="text-sm font-semibold text-text-strong">
                  {t("noColleagues")}
                </p>
              ) : (
                <SelectInput id={id} data-testid="pin-switch-colleague" value={colleague} onChange={(e) => setColleague(e.target.value)}>
                  <option value="">{t("choose")}</option>
                  {colleagues.map((s) => (
                    <option key={s.userId} value={s.userId}>
                      {s.firstName} {s.lastName}
                    </option>
                  ))}
                </SelectInput>
              )
            }
          </Field>
          <Field label={t("pin")}>
            {(id) => (
              <TextInput
                id={id}
                data-testid="pin-switch-input"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                maxLength={6}
                autoFocus
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="min-h-target-lg font-ledger text-2xl tracking-[0.3em]"
              />
            )}
          </Field>
          {problem && (
            <div className="flex flex-col gap-2">
              <p role="alert" data-testid="pin-switch-problem" className="text-sm font-semibold text-text-error">
                {problem}
              </p>
              {colleagueLocked && (
                <ActionButton onClick={() => setStep("unlock")} data-testid="pin-switch-unlock-open">
                  {t("unlock.action")}
                </ActionButton>
              )}
            </div>
          )}
          {shapeWrong && <p role="alert" className="text-sm font-semibold text-text-error">{t("pinFormat")}</p>}
          <input type="submit" hidden />
        </form>
      ) : (
        <form onSubmit={submitUnlock} className="flex flex-col gap-4" data-testid="pin-unlock-dialog">
          <Field label={t("unlock.manager")}>
            {(id) => (
              <SelectInput id={id} data-testid="pin-unlock-manager" value={manager} onChange={(e) => setManager(e.target.value)}>
                <option value="">{t("unlock.choose")}</option>
                {(staff ?? [])
                  .filter((s) => s.active && s.userId !== colleague)
                  .map((s) => (
                    <option key={s.userId} value={s.userId}>
                      {s.firstName} {s.lastName}
                    </option>
                  ))}
              </SelectInput>
            )}
          </Field>
          <Field label={t("unlock.pin")}>
            {(id) => (
              <TextInput
                id={id}
                data-testid="pin-unlock-input"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                maxLength={6}
                autoFocus
                value={managerPin}
                onChange={(e) => setManagerPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="min-h-target-lg font-ledger text-2xl tracking-[0.3em]"
              />
            )}
          </Field>
          {problem && (
            <p role="alert" data-testid="pin-unlock-problem" className="text-sm font-semibold text-text-error">
              {problem}
            </p>
          )}
          {shapeWrong && <p role="alert" className="text-sm font-semibold text-text-error">{t("pinFormat")}</p>}
          <ActionButton onClick={() => setStep("pin")}>{tc("cancel")}</ActionButton>
          <input type="submit" hidden />
        </form>
      )}
    </Modal>
  );
}
